import Header from "@editorjs/header";
import EditorjsList from "@editorjs/list";
import Raw from "@editorjs/raw";
import Table from "@editorjs/table";
import Delimiter from "@editorjs/delimiter";
import Quote from "@editorjs/quote";
import Embed from "@editorjs/embed";
import ColorPlugin from "editorjs-text-color-plugin";
import CustomImageTool from "../plugins/CustomImage";

const ColorPluginClass = (ColorPlugin as any).default || ColorPlugin;
import SmartLink from "../plugins/SmartLink";
import { FaqTool } from "../plugins/faq";
import { AccordianTool } from "../plugins/CustomAccordian";
import { AccordianConfig } from "../plugins/CustomAccordian/types";

export const tools = (
  imageUploadFn: (file: File) => Promise<{ url: string }>
) => ({
  // Overrides Editor.js's built-in inline "link" tool so every toolbar that
  // lists "link" gets the SEO-aware version (new tab + nofollow controls).
  link: {
    class: SmartLink,
  },
  table: {
    class: Table,
    inlineToolbar: true,
  },
  paragraph: {
    inlineToolbar: ["bold", "italic", "link", "Color", "Marker"],
    sanitize: {
      font: {
        color: true,
      },
      span: {
        style: true,
      },
    },
  },
  header: {
    class: Header,
    inlineToolbar: ["bold", "italic", "link", "Color", "Marker"],
    config: {
      placeholder: "Enter a header",
      levels: [1, 2, 3, 4, 5, 6],
      defaultLevel: 2,
    },
    sanitize: {
      font: {
        color: true,
      },
      span: {
        style: true,
      },
    },
  },
  list: {
    class: EditorjsList,
    inlineToolbar: true,
  },
  delimiter: Delimiter,
  quote: Quote,
  embed: Embed,
  Color: {
    class: ColorPluginClass,
    config: {
      colorCollections: [
        "#1da077",
        "#059669",
        "#0f172a",
        "#334155",
        "#ef4444",
        "#f59e0b",
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
      ],
      defaultColor: "#1A926C",
      type: "text",
      customPicker: true,
    },
  },
  Marker: {
    class: ColorPluginClass,
    config: {
      colorCollections: [
        "#1da077",
        "#fde047",
        "#86efac",
        "#93c5fd",
        "#fca5a5",
        "#d8b4fe",
      ],
      defaultColor: "#1da077",
      type: "marker",
      icon: `<svg fill="#000000" height="200px" width="200px" version="1.1" id="Icons" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 32 32" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M17.6,6L6.9,16.7c-0.2,0.2-0.3,0.4-0.3,0.6L6,23.9c0,0.3,0.1,0.6,0.3,0.8C6.5,24.9,6.7,25,7,25c0,0,0.1,0,0.1,0l6.6-0.6 c0.2,0,0.5-0.1,0.6-0.3L25,13.4L17.6,6z"></path> <path d="M26.4,12l1.4-1.4c1.2-1.2,1.1-3.1-0.1-4.3l-3-3c-0.6-0.6-1.3-0.9-2.2-0.9c-0.8,0-1.6,0.3-2.2,0.9L19,4.6L26.4,12z"></path> </g> <g> <path d="M28,29H4c-0.6,0-1-0.4-1-1s0.4-1,1-1h24c0.6,0,1,0.4,1,1S28.6,29,28,29z"></path> </g> </g></svg>`,
    },
  },
  image: {
    class: CustomImageTool,
    inlineToolbar: true,
    config: {
      captionPlaceholder: "Alt Text here...",
      hyperLink: false,
      uploader: {
        async uploadByFile(file: File) {
          return {
            success: 1,
            file: await imageUploadFn(file),
          };
        },
      },
    },
  },
  raw: {
    class: Raw,
    config: {
      contenteditable: true,
    },
  },
});

export const getCustomEditorTools = (
  imageUploadFn: (file: File) => Promise<{ url: string }>,
  {
    faqEnabled = false,
    accordianEnabled = false,
    accordianConfig,
  }: {
    faqEnabled?: boolean;
    accordianEnabled?: boolean;
    accordianConfig?: AccordianConfig;
  }
) => {
  const base = {
    ...tools(imageUploadFn),
  } as Record<string, unknown>;

  if (faqEnabled) {
    base.FAQ = {
      class: FaqTool,
      inlineToolbar: true,
    };
  }

  if (accordianEnabled) {
    base.Accordian = {
      class: AccordianTool,
      inlineToolbar: true,
      config: accordianConfig,
    };
  }

  return base;
};
